import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { reportApi } from '../../api/report.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const emptyForm = () => ({
  name: '',
  need_cost: false,
  need_company_name: false,
  need_phone_number: false,
  need_full_name: false,
  parent_id: '',
});

export default function DailyReportItemsPage() {
  const { addToast } = useUI();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await reportApi.getDailyReportItems();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (showSpinner) addToast('خطا در بارگذاری بخش‌های گزارش', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!editId || saving });

  const topLevel = items.filter((it) => !it.parent_id);

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
  };

  const startEdit = (item) => {
    setEditId(item.item_id);
    setForm({
      name: item.name,
      need_cost: item.need_cost,
      need_company_name: item.need_company_name,
      need_phone_number: item.need_phone_number,
      need_full_name: item.need_full_name,
      parent_id: item.parent_id ? String(item.parent_id) : '',
    });
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      addToast('نام بخش الزامی است', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        need_cost: form.need_cost,
        need_company_name: form.need_company_name,
        need_phone_number: form.need_phone_number,
        need_full_name: form.need_full_name,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
      };
      if (editId) {
        await reportApi.editDailyReportItem({ item_id: editId, ...payload });
        addToast('بخش ویرایش شد', 'success');
      } else {
        await reportApi.addDailyReportItem(payload);
        addToast('بخش اضافه شد', 'success');
      }
      resetForm();
      await load();
    } catch {
      addToast('خطا در ذخیره بخش', 'error');
    }
    setSaving(false);
  };

  const remove = async (itemId) => {
    if (!window.confirm('این بخش حذف شود؟')) return;
    try {
      await reportApi.deleteDailyReportItem(itemId);
      addToast('بخش حذف شد', 'success');
      if (editId === itemId) resetForm();
      await load();
    } catch {
      addToast('خطا در حذف بخش', 'error');
    }
  };

  const parentLabel = (parentId) => {
    if (!parentId) return null;
    return items.find((it) => it.item_id === parentId)?.name;
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="بخش‌های گزارش روزانه"
        subtitle="فیلدهایی که کارمندان دپارتمان شما هنگام پر کردن گزارش روزانه می‌بینند"
        action={
          <Link to="/settings" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>
            ← بازگشت به تنظیمات
          </Link>
        }
      />

      <div className="pj-split-grid">
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>بخش‌های تعریف‌شده</h3>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
          ) : items.length === 0 ? (
            <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '24px 0' }}>
              هنوز بخشی تعریف نشده — از فرم کنار صفحه اولین بخش را بسازید.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item) => (
                <div
                  key={item.item_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', borderRadius: 10,
                    border: `1.5px solid ${editId === item.item_id ? '#29AAE1' : '#e2e8f0'}`,
                    background: editId === item.item_id ? '#EDF3FC' : '#fff',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {item.parent_id ? `↳ ${item.name}` : item.name}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {parentLabel(item.parent_id) && (
                        <span style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 8 }}>
                          زیرمجموعه: {parentLabel(item.parent_id)}
                        </span>
                      )}
                      {item.need_cost && (
                        <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 8 }}>نیاز به مبلغ</span>
                      )}
                      {item.need_company_name && (
                        <span style={{ fontSize: 10, color: '#233A7A', background: '#D9E4F7', padding: '2px 8px', borderRadius: 8 }}>نیاز به نام شرکت</span>
                      )}
                      {item.need_phone_number && (
                        <span style={{ fontSize: 10, color: '#0f766e', background: '#ccfbf1', padding: '2px 8px', borderRadius: 8 }}>نیاز به شماره تماس</span>
                      )}
                      {item.need_full_name && (
                        <span style={{ fontSize: 10, color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: 8 }}>نیاز به نام و نام خانوادگی</span>
                      )}
                    </div>
                  </div>
                  <Btn size="sm" variant="secondary" onClick={() => startEdit(item)}>ویرایش</Btn>
                  <button
                    onClick={() => remove(item.item_id)}
                    style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                    title="حذف"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>
            {editId ? 'ویرایش بخش' : 'افزودن بخش جدید'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>نام بخش</span>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="مثلاً: پیگیری تیکت، بازدید حضوری..."
                style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>زیرمجموعهٔ (اختیاری)</span>
              <select
                value={form.parent_id}
                onChange={(e) => setForm((p) => ({ ...p, parent_id: e.target.value }))}
                style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
              >
                <option value="">— بخش اصلی —</option>
                {topLevel.filter((it) => it.item_id !== editId).map((it) => (
                  <option key={it.item_id} value={it.item_id}>{it.name}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.need_cost} onChange={(e) => setForm((p) => ({ ...p, need_cost: e.target.checked }))} />
              کارمند باید مبلغ/هزینه وارد کند
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.need_company_name} onChange={(e) => setForm((p) => ({ ...p, need_company_name: e.target.checked }))} />
              کارمند باید نام شرکت وارد کند
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.need_phone_number} onChange={(e) => setForm((p) => ({ ...p, need_phone_number: e.target.checked }))} />
              کارمند باید شماره تماس وارد کند
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.need_full_name} onChange={(e) => setForm((p) => ({ ...p, need_full_name: e.target.checked }))} />
              کارمند باید نام و نام خانوادگی وارد کند
            </label>

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Btn onClick={save} loading={saving}>{editId ? 'ذخیره تغییرات' : 'افزودن بخش'}</Btn>
              {editId && <Btn variant="secondary" onClick={resetForm}>انصراف</Btn>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
